import { useState, useEffect, useRef } from 'react'
import {
  Button,
  Divider,
  Input,
  Typography,
  Upload,
  Spin,
  message,
  Form,
} from 'antd'
import { UploadOutlined, DeleteOutlined } from '@ant-design/icons'
import { getMarketplaceByIds } from 'src/api/marketplace'
import { saveEditMarketplace } from 'src/api/event'
import { useNavigate, useParams } from 'react-router-dom'
import './EditMarketplaceEvent.css'
import { manualFetch } from 'src/utils/fetch'
import { validate } from 'uuid'

const { Title } = Typography

const EditMarketplaceEvent = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [form] = Form.useForm()

  const [loading, setLoading] = useState(false)
  const [imagePreviews, setImagePreviews] = useState([])
  const newImages = useRef([])

  const loadMarketplace = async () => {
    try {
      setLoading(true)

      const res = await getMarketplaceByIds([id])
      const item = res?.data?.[0]

      if (!item) {
        message.error('Marketplace item not found')
        return
      }

      form.setFieldsValue({
        name: item.name,
        points: item.points,
        description: item.description,
      })

      if (item.images?.length) {
        const previews = []

        for (let i = 0; i < item.images.length; i++) {
          const url = item.images[i]
          const response = await manualFetch(url)
          const blob = await response.blob()
          const previewUrl = URL.createObjectURL(blob)

          previews.push(previewUrl)
          newImages.current.push(
            new File([blob], `image-${i}.png`, { type: blob.type })
          )
        }

        setImagePreviews(previews)
      }
    } catch (err) {
      console.error(err)
      message.error('Failed to load marketplace')
    } finally {
      setLoading(false)
    }
  }

  const handleAddImage = ({ file }) => {
    const previewUrl = URL.createObjectURL(file)
    setImagePreviews((prev) => [...prev, previewUrl])
    newImages.current.push(file)
  }

  const handleDeleteImage = (index) => {
    setImagePreviews((prev) => {
      const updated = [...prev]
      const removed = updated.splice(index, 1)[0]
      if (removed?.startsWith('blob:')) {
        URL.revokeObjectURL(removed)
      }
      return updated
    })
    newImages.current.splice(index, 1)
  }

  const handleSave = async () => {
    try {
      const values = await form.validateFields()
      if (!newImages.current.length) {
        message.error('Please upload at least one image')
        return
      }

      setLoading(true)

      await saveEditMarketplace(
        id,
        values.name,
        values.points,
        values.description,
        newImages.current
      )

      navigate('/events/prices')
    } catch (err) {
      console.error(err)
      message.error('Failed to save marketplace')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadMarketplace()
  }, [])

  return (
    <Spin spinning={loading}>
      <div className="edit-metaverse-container">
        <Title level={3}>Edit Event Marketplace</Title>
        <Divider />
        <Form
          form={form}
          layout="horizontal"
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 18 }}
        >
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: 'Please enter name' }]}
          >
            <Input placeholder="Marketplace name" />
          </Form.Item>

          <Form.Item label="Points" name="points">
            <Input type="number" min={0} />
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
            rules={[{ required: true, message: 'Please enter description' }]}
          >
            <Input.TextArea rows={4} />
          </Form.Item>

          <Form.Item
            label="Images"
            required
            rules={[
              {
                validator: () => {
                  if (newImages.current.length === 0) {
                    return Promise.reject('Please upload at least one image')
                  }
                  return Promise.resolve()
                },
              },
            ]}
          >
            <div className="images-wrapper">
              {imagePreviews.length ? (
                imagePreviews.map((img, index) => (
                  <div key={index} className="image-container">
                    <img
                      src={img}
                      alt="marketplace"
                      style={{
                        width: 120,
                        height: 120,
                        objectFit: 'contain',
                      }}
                    />
                    <DeleteOutlined
                      onClick={() => handleDeleteImage(index)}
                      style={{ cursor: 'pointer', color: 'red' }}
                    />
                  </div>
                ))
              ) : (
                <p>No images</p>
              )}
            </div>

            <Upload
              accept="image/*"
              beforeUpload={() => false}
              showUploadList={false}
              onChange={handleAddImage}
            >
              <Button icon={<UploadOutlined />}>Add Image</Button>
            </Upload>
          </Form.Item>
          <Form.Item wrapperCol={{ offset: 6 }}>
            <Button
              type="primary"
              style={{ width: '20%' }}
              className="btn-primary"
              onClick={handleSave}
            >
              Save
            </Button>
            <Button
              style={{ marginLeft: 8, width: '20%' }}
              onClick={() => navigate('/events/prices')}
            >
              Cancel
            </Button>
          </Form.Item>
        </Form>{' '}
      </div>
    </Spin>
  )
}

export default EditMarketplaceEvent
